import type { MouseEvent } from 'react';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';

import { Box, Text, Stack, Inline } from '@atlaskit/primitives';
import { Pressable } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Field } from '@atlaskit/form';
import SpeedometerLeftIcon from '@atlaskit/icon-lab/core/speedometer-left';
import SpeedometerUpIcon from '@atlaskit/icon-lab/core/speedometer-up';
import SpeedometerRightIcon from '@atlaskit/icon-lab/core/speedometer-right';

const i18n = defineMessages({
	audioDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration-label',
		defaultMessage: 'How long is the audio?',
		description: 'Label for a Select component used for selecting audio duration',
	},
	shortDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.short',
		defaultMessage: '<b>2-3</b> \n minutes',
		description: 'Label for a short audio duration option: 2 to 3 minutes',
	},
	mediumDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.medium',
		defaultMessage: '<b>5-7</b> \n minutes',
		description: 'Label for a medium audio duration option: 5 to 7 minutes',
	},
	longDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.long',
		defaultMessage: '<b>10-12</b> \n minutes',
		description: 'Label for a long audio duration option: 10 to 12 minutes',
	},
});

interface DurationOption {
	label: string;
	icon: React.ElementType;
}

const styles = cssMap({
	selectedDurationOption: {
		flex: 1,
		backgroundColor: token('color.background.selected'),
		borderRadius: token('border.radius.100'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.075'),
	},
	durationOption: {
		flex: 1,
		backgroundColor: token('elevation.surface'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.075'),
		borderColor: token('color.border'),
		borderRadius: token('border.radius.100'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
});

// Labels are purposely string values rather than Message objects
// This allows us to call FormattedMessage where needed
const durationOptions: DurationOption[] = [
	{
		label: 'shortDurationLabel',
		icon: SpeedometerLeftIcon,
	},
	{
		label: 'mediumDurationLabel',
		icon: SpeedometerUpIcon,
	},
	{
		label: 'longDurationLabel',
		icon: SpeedometerRightIcon,
	},
];

const DurationOptionComponent = ({
	duration,
	setDuration,
	icon: Icon,
	changeHandler,
	option,
	isSelected = false,
}: {
	duration: string;
	setDuration: (value: string) => void;
	icon: React.ElementType;
	changeHandler: Function;
	option: DurationOption;
	isSelected?: boolean;
}) => {
	const handleClick = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setDuration(duration);
			changeHandler(duration);
		},
		[duration, setDuration, changeHandler],
	);

	return (
		<Pressable
			xcss={isSelected ? styles.selectedDurationOption : styles.durationOption}
			onClick={handleClick}
		>
			<Stack>
				<Box paddingBlockEnd="space.075">
					<Icon color={token('color.icon')} label="" />
				</Box>
				<Text color="color.text.subtlest">
					<FormattedMessage
						{...i18n[option.label]}
						values={{
							b: (chunks: React.ReactNode[]) => (
								<Text weight="semibold" color="color.text.subtle">
									{chunks}
								</Text>
							),
						}}
					/>
				</Text>
			</Stack>
		</Pressable>
	);
};

export const DurationSelector = () => {
	const { formatMessage } = useIntl();
	const [selectedDuration, setSelectedDuration] = useState(formatMessage(i18n.mediumDurationLabel));

	const handleSetDuration = useCallback((duration: string) => {
		setSelectedDuration(duration);
	}, []);

	return (
		<Field
			name="audio-duration"
			defaultValue={selectedDuration}
			label={formatMessage(i18n.audioDurationLabel)}
		>
			{({ fieldProps }) => (
				<Box data-name={fieldProps.id} data-value={fieldProps.value}>
					<Inline grow="fill" spread="space-between" space="space.100">
						{durationOptions.map((option) => {
							const duration = formatMessage(i18n[option.label]);
							return (
								<DurationOptionComponent
									key={duration}
									duration={duration}
									setDuration={handleSetDuration}
									icon={option.icon}
									isSelected={duration === selectedDuration}
									changeHandler={fieldProps.onChange}
									option={option}
								/>
							);
						})}
					</Inline>
				</Box>
			)}
		</Field>
	);
};
